import { useEffect, useId, useRef, type ReactNode } from "react";

const FOCUSABLE_SELECTOR = "input, select, textarea, button, [href], [tabindex]:not([tabindex='-1'])";

type Props = {
  title: string;
  children: ReactNode;
  onClose: () => void;
  primaryLabel?: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  secondaryLabel?: string;
  danger?: boolean;
  wide?: boolean;
};

export function Modal({
  title,
  children,
  onClose,
  primaryLabel,
  onPrimary,
  primaryDisabled,
  secondaryLabel = "Cancel",
  danger,
  wide,
}: Props) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // Keep onClose in a ref so the focus/keydown effect can run only once on mount.
  // Depending on the onClose prop (an inline arrow from the parent) would re-run
  // this effect on every parent render (e.g. each keystroke), stealing focus.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const root = panelRef.current;
    // Only pull focus into the dialog if it is not already there. This makes the
    // effect safe to run more than once: it can never yank the caret out of a
    // field the user is typing in (the root cause of the "typing stops after
    // each letter" bug).
    if (root && !root.contains(document.activeElement)) {
      const focusTarget =
        root.querySelector<HTMLElement>("input, select, textarea") ??
        root.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      focusTarget?.focus();
    }

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !root) return;
      const nodes = [...root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
        (el) => !el.hasAttribute("disabled"),
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, []);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className={`modal-panel${wide ? " modal-panel--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="modal-panel__head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="ghost modal-panel__close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="modal-panel__body">{children}</div>
        {(primaryLabel || secondaryLabel) && (
          <footer className="modal-panel__foot">
            <button type="button" className="secondary" onClick={onClose}>
              {secondaryLabel}
            </button>
            {primaryLabel && onPrimary ? (
              <button
                type="button"
                className={danger ? "danger" : undefined}
                disabled={primaryDisabled}
                onClick={onPrimary}
              >
                {primaryLabel}
              </button>
            ) : null}
          </footer>
        )}
      </div>
    </div>
  );
}
